import type { SessionWorktreeManager } from '../../worktrees/manager.js';
import { log } from '../../log.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { fileBrowserReadInFlight } from '../../files/browser/in-flight.js';
import { CodeNavCacheUnavailableError } from '../../sqlite/code-nav-cache/database.js';
import { logCodeNavCacheUnavailableFromError } from '../../sqlite/code-nav-cache/code-nav-cache-log.js';
import { listDirectImportTargetsForFileAsync } from '../symbol-index/query/list-direct-import-targets.js';
import {
  scheduleSymbolIndexFile,
  scheduleSymbolIndexImportNeighbors,
} from '../symbol-index/scheduler/symbol-index-scheduler.js';
import { symbolIndexFileIsReadyProbe } from '../symbol-index/lifecycle/symbol-index-file-ready.js';
import { symbolIndexWorkerClient } from '../symbol-index/worker/client.js';
import { waitForSymbolIndexReady } from './wait-for-symbol-index-ready.js';
import {
  buildPreparedCodeNavBaseContext,
  type PreparedCodeNavRequest,
} from './prepare-code-nav-base-context.js';
import { normalizeCodeNavOp } from './code-nav-op-context.js';
import { resolveCodeNavParentPath } from './resolve-code-nav-parent.js';
import type { CodeNavRequestMessage } from './types.js';

export type { PreparedCodeNavRequest } from './prepare-code-nav-base-context.js';

function scheduleSymbolIndexAfterResponse(parentPath: string, reqPath: string): void {
  setImmediate(() => scheduleSymbolIndexFile(parentPath, reqPath));
}

function scheduleImportNeighborsAfterResponse(parentPath: string, reqPath: string): void {
  setImmediate(() => {
    void listDirectImportTargetsForFileAsync(parentPath, reqPath)
      .then((importTargets) => scheduleSymbolIndexImportNeighbors(parentPath, importTargets))
      .catch((err) => {
        if (err instanceof CodeNavCacheUnavailableError) {
          logCodeNavCacheUnavailableFromError(log, err);
        }
      });
  });
}

export async function prepareCodeNavRequest(
  msg: CodeNavRequestMessage,
  sessionWorktreeManager: SessionWorktreeManager | undefined,
): Promise<PreparedCodeNavRequest | { error: string }> {
  await yieldToEventLoop();
  const reqPath = msg.path.replace(/^\/+/, '') || '';
  if (!reqPath) {
    return { error: 'Path required' };
  }

  const sessionParentPath = await resolveCodeNavParentPath(sessionWorktreeManager, msg.sessionId);
  symbolIndexWorkerClient.registerParent(sessionParentPath);

  const baseContext = buildPreparedCodeNavBaseContext(msg, reqPath, sessionParentPath);
  const op = normalizeCodeNavOp(msg.op);

  if (op === 'symbols') {
    if (fileBrowserReadInFlight()) {
      scheduleSymbolIndexAfterResponse(sessionParentPath, reqPath);
      return { ...baseContext, indexReady: false };
    }

    const indexReady = await symbolIndexFileIsReadyProbe(sessionParentPath, reqPath);
    if (!indexReady) {
      scheduleSymbolIndexAfterResponse(sessionParentPath, reqPath);
      return { ...baseContext, indexReady: false };
    }

    scheduleImportNeighborsAfterResponse(sessionParentPath, reqPath);
    return { ...baseContext, indexReady: true };
  }

  scheduleSymbolIndexAfterResponse(sessionParentPath, reqPath);
  const indexReady = await waitForSymbolIndexReady(sessionParentPath, reqPath);
  if (indexReady) {
    scheduleImportNeighborsAfterResponse(sessionParentPath, reqPath);
  }
  await yieldToEventLoop();

  return {
    ...baseContext,
    indexReady,
  };
}

export function preparedOpContext(ctx: PreparedCodeNavRequest) {
  return {
    msg: { ...ctx.msg, op: normalizeCodeNavOp(ctx.msg.op) },
    reqPath: ctx.reqPath,
    sessionParentPath: ctx.sessionParentPath,
    line: ctx.line,
    column: ctx.column,
  };
}
